/**
 * BottomNav - Bottom tab navigation bar
 */
import React from 'react';
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Icon, IconName } from './Icon';
import { colors, SAFE_BOTTOM } from '../theme';
import type { RootStackParamList } from '../types/navigation';

type TabItem = {
  id: keyof RootStackParamList;
  label: string;
  icon: IconName;
  filledIcon?: IconName;
};

const TABS: TabItem[] = [
  { id: 'M03HomeScreen',        label: 'Trang chủ', icon: 'home',     filledIcon: 'home' },
  { id: 'M04ProductListScreen',  label: 'Sản phẩm',  icon: 'list',     filledIcon: 'list' },
  { id: 'M11HistoryScreen',      label: 'Lịch sử',   icon: 'history',  filledIcon: 'history' },
  { id: 'M12ProfileScreen',      label: 'Hồ sơ',     icon: 'person',   filledIcon: 'person' },
];

const NAV_BG = 'rgba(255,255,255,0.92)';

export const BottomNav: React.FC = () => {
  const navigation = useNavigation<any>();
  const insets = useSafeAreaInsets();
  const bottomPad = Math.max(insets.bottom, SAFE_BOTTOM);

  // Get active route name from navigation state
  const navState = (navigation as any).getState?.();
  const screenName = navState?.routes?.[navState?.index ?? 0]?.name as keyof RootStackParamList | undefined;

  // Render bottom nav on all screens except splash and login
  const showNav = screenName && screenName !== 'M01SplashScreen' && screenName !== 'M02LoginScreen';
  if (!showNav) return null;

  const getActiveTab = (currentScreen: string): string => {
    if (currentScreen === 'M03HomeScreen') return 'M03HomeScreen';
    if (
      [
        'M04ProductListScreen',
        'M05ProductDetailScreen',
        'M06InputCustomerScreen',
        'M07ResultScreen',
        'M08CompareScreen',
        'M09PdfScreen',
        'M10SaveCustomerScreen',
      ].includes(currentScreen)
    ) {
      return 'M04ProductListScreen';
    }
    if (currentScreen === 'M11HistoryScreen') return 'M11HistoryScreen';
    if (currentScreen === 'M12ProfileScreen') return 'M12ProfileScreen';
    return '';
  };

  const activeTabId = screenName ? getActiveTab(screenName) : '';

  return (
    <View
      style={[
        styles.wrap,
        {
          paddingBottom: bottomPad,
          backgroundColor: NAV_BG,
          borderTopWidth: StyleSheet.hairlineWidth,
          borderTopColor: 'rgba(0,51,102,0.1)',
        },
      ]}
    >
      {TABS.map((tab) => {
        const isActive = activeTabId === tab.id;
        return (

          <TouchableOpacity
            key={tab.id}
            style={[styles.item, isActive && styles.itemActive]}
            onPress={() => navigation.navigate(tab.id)}
            activeOpacity={0.7}
            accessibilityRole="tab"
            aria-selected={isActive}
          >
            <Icon
              name={isActive ? (tab.filledIcon ?? tab.icon) : tab.icon}
              size={24}
              color={isActive ? colors.secondary : '#9e9e9e'}
              filled={isActive}
            />
            <Text
              style={[
                styles.label,
                isActive ? styles.labelActive : styles.labelInactive,
              ]}
            >
              {tab.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingTop: 6,
    paddingHorizontal: 16,
    // @ts-ignore backdropFilter is a web-only property
    backdropFilter: 'blur(20px)',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    borderRadius: Platform.OS === 'web' ? 0 : 24,
  },
  item: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 12,
    minWidth: 64,
    flex: 1,
  },
  itemActive: {
    backgroundColor: 'rgba(0,166,81,0.08)',
    transform: [{ scale: 0.92 }],
  },
  label: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.04,
    textTransform: 'uppercase',
    marginTop: 3,
  },
  labelActive: {
    color: colors.secondary,
  },
  labelInactive: {
    color: '#9e9e9e',
  },
});
